import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const ImageLightbox = ({
  open,
  src,
  alt = '',
  caption,
  onClose,
  initialScale = 1,
  minScale = 1,
  maxScale = 5,
  step = 0.25,
}) => {
  const [scale, setScale] = useState(initialScale);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const draggingRef = useRef({ active: false, startX: 0, startY: 0, originX: 0, originY: 0 });

  const canRender = open && !!src;

  const resetView = () => {
    setScale(initialScale);
    setTranslate({ x: 0, y: 0 });
  };

  useEffect(() => {
    if (!open) return;
    resetView();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose && onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  const zoomIn = () => setScale((s) => clamp(Number((s + step).toFixed(2)), minScale, maxScale));
  const zoomOut = () => setScale((s) => clamp(Number((s - step).toFixed(2)), minScale, maxScale));

  const transformStyle = useMemo(() => {
    return {
      transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
      cursor: scale > 1 ? (draggingRef.current.active ? 'grabbing' : 'grab') : 'default',
    };
  }, [scale, translate.x, translate.y]);

  const onWheel = (e) => {
    e.preventDefault();
    const dir = e.deltaY < 0 ? 1 : -1;
    setScale((s) => clamp(Number((s + dir * step).toFixed(2)), minScale, maxScale));
  };

  const onPointerDown = (e) => {
    if (scale <= 1) return;
    e.preventDefault();
    draggingRef.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      originX: translate.x,
      originY: translate.y,
    };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (_) {}
  };

  const onPointerMove = (e) => {
    if (!draggingRef.current.active) return;
    e.preventDefault();
    const dx = e.clientX - draggingRef.current.startX;
    const dy = e.clientY - draggingRef.current.startY;
    setTranslate({
      x: draggingRef.current.originX + dx,
      y: draggingRef.current.originY + dy,
    });
  };

  const endDrag = () => {
    draggingRef.current.active = false;
  };

  if (!canRender) return null;

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center"
      style={{ zIndex: 9999 }}
      onClick={() => onClose && onClose()}
    >
      <div
        className="relative w-[95vw] h-[92vh] max-w-6xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute top-3 right-3 flex items-center gap-2 z-10">
          <button
            type="button"
            className="p-2 rounded-md bg-white/10 hover:bg-white/20 text-white"
            onClick={zoomOut}
            aria-label="Dézoomer"
          >
            <ZoomOut size={18} />
          </button>
          <button
            type="button"
            className="p-2 rounded-md bg-white/10 hover:bg-white/20 text-white"
            onClick={zoomIn}
            aria-label="Zoomer"
          >
            <ZoomIn size={18} />
          </button>
          <button
            type="button"
            className="p-2 rounded-md bg-white/10 hover:bg-white/20 text-white"
            onClick={resetView}
            aria-label="Réinitialiser"
          >
            <RotateCcw size={18} />
          </button>
          <button
            type="button"
            className="p-2 rounded-md bg-white/10 hover:bg-white/20 text-white"
            onClick={() => onClose && onClose()}
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>

        <div className="absolute top-3 left-3 z-10">
          <div className="px-3 py-1.5 rounded-md bg-white/10 text-white text-xs">
            {Math.round(scale * 100)}%
          </div>
        </div>

        <div className="w-full h-full overflow-hidden rounded-lg bg-black flex items-center justify-center">
          <div
            className="w-full h-full flex items-center justify-center"
            onWheel={onWheel}
          >
            <img
              src={src}
              alt={alt}
              className="max-w-full max-h-full select-none"
              draggable={false}
              style={transformStyle}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              onPointerLeave={endDrag}
            />
          </div>
        </div>

        {(caption || alt) && (
          <div className="mt-2 text-center text-sm text-white/90">
            {caption || alt}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageLightbox;
